<script lang="ts" setup>
import { useRouter } from 'vue-router';
import type { z } from 'zod';

import type { FormErrorEvent } from '#ui/types';
import { RegisterSchema } from '~/schemas/auth';

definePageMeta({
  middleware: 'auth-and-menu'
});

type State = Partial<z.infer<typeof RegisterSchema>>;

const router = useRouter();
const authStore = useAuthStore();
const preferenceStore = usePreferenceStore();

useSeoMeta({
  title: 'Sign up'
});

const state = reactive<State>({});

const schemaError = ref<string>('');

const handleSubmit = async (): Promise<void> => {
  if (!state.email) {
    return;
  }

  await authStore.register({
    username: state.email,
    email: state.email,
    password: state.password
  });

  authStore.guestLogout();

  if (preferenceStore.preference.general.enableEmailVerification) {
    await router.push({
      name: 'login-confirmemail',
      query: { email: state.email }
    });

    return;
  }

  await authStore.login(state.email, state.password!);
  await router.push({ name: 'index' });
};

const onSubmit = (): void => {
  handleSubmit();
};

const onError = (event: FormErrorEvent) => {
  schemaError.value = event.errors.find(error => !error.id)?.message ?? '';
};
</script>

<template>
  <UContainer class="register min-h-svh flex flex-col items-center">
    <UForm
      class="py-4 sm:pt-20 max-w-[30rem] w-full"
      :schema="RegisterSchema"
      :state="state"
      @submit="onSubmit()"
      @error="onError"
    >
      <UCard
        class="shadow-2xl"
        :ui="{ body: { base: 'space-y-4' } }"
      >
        <template #header>
          <div class="text-center">
            <h1 class="font-semibold text-2xl">
              Create a new account
            </h1>
          </div>
        </template>

        <UFormGroup
          label="Email"
          name="email"
        >
          <UInput
            v-model="state.email"
            icon="ic:round-person"
            size="xl"
            data-test="email"
          />
        </UFormGroup>

        <UFormGroup
          label="Password"
          name="password"
        >
          <UInput
            v-model="state.password"
            icon="ic:round-lock"
            type="password"
            size="xl"
            data-test="password"
          />
        </UFormGroup>

        <UFormGroup
          label="Confirm your password"
          name="confirm"
          :error="schemaError"
          data-test="confirm-field"
        >
          <UInput
            v-model="state.confirm"
            icon="ic:round-lock"
            type="password"
            size="xl"
            data-test="confirm"
          />
        </UFormGroup>

        <template #footer>
          <UButton
            block
            class="bg-company-1 py-4 font-semibold uppercase"
            label="Sign Up"
            type="submit"
            data-test="submit"
          />

          <div class="flex flex-col py-2 text-sm">
            <span>
              By creating an account you agree to our

              <NuxtLink
                class="font-semibold text-company-1 cursor-pointer"
                data-test="terms-conditions"
                :to="{ name: 'termsandconditions' }"
              >
                Terms & Conditions
              </NuxtLink>
              <span>.</span>
            </span>

            <span>
              Also please read our

              <NuxtLink
                class="font-semibold text-company-1 cursor-pointer"
                data-test="privacy-policy"
                :to="{ name: 'privacypolicy' }"
              >
                Privacy Policy
              </NuxtLink>
              <span>.</span>
            </span>

            <br>

            <span>
              Already have an account?

              <NuxtLink
                class="font-semibold text-company-1 cursor-pointer"
                data-test="sign-in"
                :to="{ name: 'login' }"
              >
                Sign In
              </NuxtLink>
            </span>
          </div>
        </template>
      </UCard>
    </UForm>
  </UContainer>
</template>

<style lang="scss" scoped>
.register {
  padding-top: var(--header-height);
}
</style>
